import { useEffect, useCallback, useRef, useState, useMemo } from "react";
import api from "api/api";

// redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

// Echarts for react
import ReactECharts from "echarts-for-react";

// common components
import Loader from "../common/Loader";

// utils
import { hslToCss } from "utils/analysisTable/analysisUtils";

//Props Validations
import PropTypes from "prop-types";

const getProcessedFilters = (filters) => {
    const result = {};
    Object.entries(filters).forEach(([filterKey, values]) => {
        const checkedValues = values.filter((v) => v.checked).map((v) => v.label);

        if (checkedValues.length) {
            result[filterKey] = checkedValues.join(", ");
        }
    });
    return result;
};

const getSortedData = (data, draggableColumns) => {
    return draggableColumns.map((f) => data.find((s) => s.title === f.title)).filter(Boolean);
};

function PercentageScoreDistributionChart({ selectedStudy = [], likertScaleData = [], scoreDistributionGroups = [], scoreDistributionSelectedVariable = {}, draggableColumns = {} }) {
    const chartRef = useRef(null);
    const dispatch = useDispatch();

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const coreDataId = selectedStudy[0]?.id || null;
    const scaleValue = likertScaleData[scoreDistributionSelectedVariable?.value] ?? 0;

    const consumerFilters = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    const productColors = useSelector((state) => state.analysisResultsStore.productColors);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const calculateScoreDistributionGroups = useMemo(() => {
        const result = [];
        const mid = Math.ceil(scaleValue / 2);

        for (let i = 1; i <= scaleValue; ) {
            if (scaleValue % 2 !== 0 && i === mid) {
                result.push(`${i}`);
                i += 1;
            } else {
                result.push(`${i},${i + 1}`);
                i += 2;
            }
        }

        return result;
    }, [scaleValue]);

    const consumerFilterPayload = useCallback(() => {
        return Object.entries(consumerFilters)
            .map(([product_code, { filters }]) => {
                const product = product_code.split("__")[0].replaceAll("_", " ");
                const result = getProcessedFilters(filters);

                return Object.keys(result).length ? { product_code: product, ...result } : null;
            })
            .filter(Boolean);
    }, [consumerFilters]);

    const fetchData = useCallback(async () => {
        if (Object.keys(scoreDistributionSelectedVariable).length > 0) {
            setIsLoading(true);
            const groupsData = scoreDistributionGroups.length > 0 ? scoreDistributionGroups : calculateScoreDistributionGroups;
            try {
                const url = process.env.REACT_APP_SERVER_PATH + `/secondary-study-datas/score-distribution/`;
                const payload = {
                    core_data_id: coreDataId,
                    backend_variable: scoreDistributionSelectedVariable.value,
                    groups: groupsData,
                    consumer_filters: consumerFilterPayload(),
                    product_category: productCategoryId,
                };
                await api
                    .post(url, payload)
                    .then((res) => {
                        const sortedData = getSortedData(res.data, draggableColumns);
                        setData(sortedData);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    }, [coreDataId, productCategoryId, handleError, scoreDistributionGroups, scoreDistributionSelectedVariable, consumerFilterPayload, calculateScoreDistributionGroups, draggableColumns]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getUpdatedData = (data, group) => {
        const updatedData = data.map((item) => {
            const score = item.scores.find((s) => s.group === group);
            const count = score?.count;

            return count;
        });

        return updatedData;
    };

    const getOptionData = () => {
        const groupsData = scoreDistributionGroups.length > 0 ? scoreDistributionGroups : calculateScoreDistributionGroups;
        const updatedData = data
            .map((datum) => {
                const isHidden = !productColumnVisibility[`checked--${datum.title.split(" ").join("_")}__${datum.coreDataId}`];
                if (isHidden) {
                    return datum;
                }
            })
            .filter((datum) => datum);

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                formatter: (params) => {
                    if (!params?.length) return "";

                    const axisTitle = params[0].axisValue;
                    const sampleSize = updatedData.find((datum) => `${datum.title} /${datum.study_reference_title}` === axisTitle)?.sampleSize;
                    const rows = params.map(
                        (p) => `
                        <div style="
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            gap: 12px;
                            margin-right: 12px;
                        ">
                            <span>${p.marker} ${p.seriesName}</span>
                            <span>${p.value}</span>
                        </div>
                    `
                    );

                    return `
                        <div style="font-weight: 600;">
                            ${axisTitle}
                        </div>
                        <div style="font-size: 12px;">
                            Sample Size releated to product – ${sampleSize}
                        </div>
                        ${rows.join("")}
                    `;
                },
            },
            legend: {
                selectedMode: false,
                bottom: 0,
            },
            grid: {
                left: "4%",
                right: "4%",
                bottom: "8%",
                containLabel: true,
            },
            xAxis: {
                type: "value",
                show: false,
            },
            yAxis: {
                type: "category",
                inverse: true,
                data: updatedData.map((datum) => `${datum.title} /${datum.study_reference_title}`),
                axisLabel: {
                    overflow: "break",
                },
            },
            series: groupsData.map((group, i) => ({
                name: group,
                type: "bar",
                stack: "total",
                label: {
                    show: true,
                    formatter: (params) => {
                        return params.value > 0 ? params.value : "";
                    },
                },
                itemStyle: {
                    color: hslToCss(...productColors[i]),
                },
                emphasis: { focus: "series" },
                data: getUpdatedData(updatedData, group),
            })),
        };
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                style={{
                    position: "relative",
                    marginBottom: "40px",
                }}
            >
                <div>
                    {Object.keys(scoreDistributionSelectedVariable).length > 0 ? (
                        <ReactECharts
                            ref={chartRef}
                            option={getOptionData()}
                            style={{
                                minHeight: "600px",
                                height: "100%",
                            }}
                            opts={{ renderer: "svg" }}
                            notMerge={true}
                            replaceMerge={["series"]}
                        />
                    ) : (
                        <p
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "600px",
                                height: "100%",
                                fontSize: "20px",
                            }}
                        >
                            Select a variable to perform &quot; % Score Distribution &quot;
                        </p>
                    )}
                </div>
            </div>
        </>
    );
}

PercentageScoreDistributionChart.propTypes = {
    selectedStudy: PropTypes.array,
    likertScaleData: PropTypes.array,
    scoreDistributionGroups: PropTypes.array,
    scoreDistributionSelectedVariable: PropTypes.object,
    draggableColumns: PropTypes.object,
};

export default PercentageScoreDistributionChart;
